'use client';

import { useEffect, useRef, useState } from 'react';
import { estimateVideoLatency, type VideoLatencyEstimate } from '@/lib/video-latency';

interface HlsVideoProps {
  /** HLS (.m3u8) or direct .mp4 URL. */
  url: string;
  label: string;
  className?: string;
  controls?: boolean;
  /**
   * Called about once a second with how far this player is behind the live
   * edge, or null when nothing usable could be read yet.
   *
   * Optional because most callers only want the picture. Manual flight wants
   * the number: an HLS picture is seconds old, and an operator flying by hand
   * has to be told by how much (see `lib/video-latency.ts` for what that figure
   * does and does not include).
   */
  onLatencyEstimate?: (estimate: VideoLatencyEstimate | null) => void;
  /**
   * Called whenever this player's own state changes.
   *
   * Optional, and only one caller wants it: manual flight overlays a latency
   * caveat ON the picture, and "video latency not measured — situational only"
   * stamped across "stream offline" is a caveat about nothing. The overlay
   * cannot see this state from outside, and it is the player that knows it.
   */
  onStreamState?: (state: HlsStreamState) => void;
}

export type HlsStreamState = 'loading' | 'playing' | 'error';

/** How often the live-edge distance is re-read, ms. */
const LATENCY_SAMPLE_MS = 1000;

/**
 * Reconnect budget for a fatal network error.
 *
 * Sized against the failure it exists for: the RTMP publisher dropping and
 * MediaMTX rebuilding the HLS muxer. Measured on 2026-09-01, the aircraft's
 * uplink died three times in twelve minutes and the publisher was back inside
 * 3-8 seconds each time, so the first two or three attempts carry almost every
 * real case. Doubling from 1 s and capping at 10 s spends 1+2+4+8+16x10 = 175 s
 * (2.9 min) before calling the stream offline - long enough to ride out a
 * reconnect, short enough that a genuinely dead feed still says so.
 */
const RECONNECT_BASE_MS = 1000;
const RECONNECT_MAX_MS = 10_000;
const RECONNECT_MAX_ATTEMPTS = 20;

/** A url that has never yielded a fragment gets this many tries instead. It is
 *  not recovering from anything; it never started. Three attempts is ~7 s. */
const RECONNECT_FIRST_LOAD_ATTEMPTS = 3;

/** End of the last range in a TimeRanges, or null when it is empty. */
function lastRangeEnd(ranges: TimeRanges | null | undefined): number | null {
  if (!ranges || ranges.length === 0) return null;
  try {
    return ranges.end(ranges.length - 1);
  } catch {
    // `end()` throws IndexSizeError if the range list changed under us between
    // the length read and the call. One missed sample, not a broken player.
    return null;
  }
}

/**
 * Plays an HLS stream via hls.js (dynamically imported), falling back to native
 * playback on Safari. Shared by VideoPanel (drone streams) and DockCommandBar
 * (dock cameras) so attach/teardown, autoplay, and offline handling live in one
 * place. A stream that never arrives (Pi down, MediaMTX not publishing, bad URL)
 * surfaces a visible "stream offline" state instead of a silent black tile.
 */
export function HlsVideo({
  url,
  label,
  className,
  controls = true,
  onLatencyEstimate,
  onStreamState,
}: HlsVideoProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [state, setState] = useState<HlsStreamState>('loading');

  // Through a ref so the attach effect keeps depending on `url` alone: a caller
  // passing an inline callback must not tear down and re-attach the player on
  // every render of its own.
  const onLatencyRef = useRef(onLatencyEstimate);
  onLatencyRef.current = onLatencyEstimate;

  // Through a ref for the same reason, and reported from an effect rather than
  // from the setters so the caller is told once per actual change — including
  // the reset to `loading` when the url changes under it.
  const onStreamStateRef = useRef(onStreamState);
  onStreamStateRef.current = onStreamState;
  useEffect(() => {
    onStreamStateRef.current?.(state);
  }, [state]);

  useEffect(() => {
    const el = videoRef.current;
    if (!el) return;

    setState('loading');
    let cancelled = false;
    let hls: import('hls.js').default | null = null;
    let detach: (() => void) | null = null;
    let attempts = 0;
    let hasPlayed = false;
    let mediaRecovered = false;
    let reconnectTimer: number | null = null;

    // Sampled rather than event-driven: hls.js has no "latency changed" event,
    // and a per-frame read would re-render the whole badge at display rate for
    // a number that only matters to a tenth of a second.
    const sample = window.setInterval(() => {
      const report = onLatencyRef.current;
      if (!report || cancelled) return;
      report(
        estimateVideoLatency({
          hlsLatencySeconds: hls?.latency,
          currentTime: el.currentTime,
          seekableEnd: lastRangeEnd(el.seekable),
          bufferedEnd: lastRangeEnd(el.buffered),
        }),
      );
    }, LATENCY_SAMPLE_MS);

    const startPlayback = () => {
      setState('playing');
      // muted autoplay is permitted without a user gesture; ignore the rejection
      // that still happens on some browsers (user can press play if controls show).
      void el.play().catch(() => undefined);
    };

    void import('hls.js').then(({ default: Hls }) => {
      if (cancelled || !videoRef.current) return;

      if (Hls.isSupported()) {
        hls = new Hls({
          enableWorker: true,
          lowLatencyMode: true,
          // Sit near the live edge, in SECONDS rather than segment counts.
          //
          // `liveSyncDurationCount: 2` was throwing away the low-latency
          // variant we pay for on both ends. A count is multiplied by the
          // TARGETDURATION - 1 s here - so it pinned the player a full 2 s
          // behind and overrode LL-HLS's own PART-HOLD-BACK, which MediaMTX
          // advertises at ~3 parts (600 ms) because hlsPartDuration is 200 ms.
          // The 6-count ceiling then allowed a further drift to 6 s before any
          // correction, and the correction is a 1.5x crawl: measured on prod
          // 2026-09-02 as an operator watching an aircraft on the pad
          // describing "a huge delay and it's not stable".
          //
          // 1 s target keeps a part-sized cushion over the 600 ms floor, which
          // a cellular uplink needs; 4 s is where we stop tolerating drift and
          // speed up. Both are seconds, so neither moves if the segment
          // duration is ever retuned.
          liveSyncDuration: 1,
          liveMaxLatencyDuration: 4,
          maxLiveSyncPlaybackRate: 1.5,
        });
        const onManifest = () => startPlayback();
        // A fragment actually buffered is the only honest "we recovered" signal.
        // Resetting the attempt count on MANIFEST_PARSED instead would let a
        // stream that parses and then dies loop forever, one manifest at a time.
        const onFragBuffered = () => {
          attempts = 0;
          mediaRecovered = false;
          hasPlayed = true;
          // A DECODED, APPENDED fragment is the honest "painting again". This is
          // the only signal that comes back after recoverMediaError(), which
          // rebuilds the buffer WITHOUT re-parsing the manifest - so
          // MANIFEST_PARSED never re-fires and a state set to 'loading' on the
          // media path would otherwise stick there for good. setState with an
          // unchanged value is a no-op, so calling it per fragment costs nothing.
          setState('playing');
        };
        const onError = (_e: unknown, data: { fatal?: boolean; type?: string; details?: string }) => {
          if (!data.fatal || cancelled) return;
          console.warn('HLS fatal error', data.type, data.details);

          // MEDIA_ERROR is a decoder desync; hls.js can rebuild the buffer in
          // place. One attempt, because a second recoverMediaError on a stream
          // that did not recover is how you get a silent reload loop.
          if (data.type === Hls.ErrorTypes.MEDIA_ERROR && !mediaRecovered) {
            mediaRecovered = true;
            // NOT GRABBABLE until a fragment lands again. Returning without
            // touching the state left it at 'playing' while the decoder was
            // stopped, and callers derive `pictureLive` from exactly that - so
            // the gesture surface stayed mounted over a frozen frame. Same
            // hazard as the network path, and worse in one respect: this branch
            // has no budget, so if hls.js recovers a stall by nudging
            // currentTime rather than re-erroring, nothing would ever have
            // corrected it.
            setState('loading');
            hls?.recoverMediaError();
            return;
          }

          // NETWORK_ERROR is the one that matters here. When the RTMP publisher
          // drops, MediaMTX destroys the HLS muxer and builds a new one on
          // reconnect — 2026-09-01 saw three of those in twelve minutes, at
          // shortening intervals. The variant playlist the player is polling
          // (`video1_stream.m3u8`) then 404s while the MASTER (`index.m3u8`)
          // keeps answering 200, so the picture died with a live stream behind
          // it and never came back: any fatal error used to be terminal.
          //
          // loadSource, NOT startLoad. startLoad resumes on the level already
          // chosen, which is the dead variant; re-reading the master is what
          // picks up the muxer's new one.
          // A stream that has NEVER buffered a fragment is not a reconnect, it
          // is a feed that was never there: Pi down, MediaMTX not publishing,
          // wrong URL. Spending the full budget on it means ~3 minutes of
          // "Connecting…" before anyone is told to go look at the hardware.
          const budget = hasPlayed ? RECONNECT_MAX_ATTEMPTS : RECONNECT_FIRST_LOAD_ATTEMPTS;
          if (data.type === Hls.ErrorTypes.NETWORK_ERROR && attempts < budget) {
            const delay = Math.min(RECONNECT_BASE_MS * 2 ** attempts, RECONNECT_MAX_MS);
            attempts += 1;
            // `loading`, not `error`: a publisher that reconnects in seconds is
            // not an offline stream, and "Stream offline" over a feed that is
            // about to return sends an operator chasing an aircraft fault that
            // does not exist.
            //
            // This state is NOT grabbable, and that is the callers' job, not
            // this one's. Every consumer derives `pictureLive` from the state
            // reported here, and they must test for `'playing'` — never for
            // "not 'error'", which was true throughout a reconnect and left the
            // gesture surface mounted over hls.js's last decoded frame. Dragging
            // a still image while a live gimbal slews is precisely the hazard F9
            // exists to prevent.
            setState('loading');
            // Overlapping fatal errors are normal — the video and audio playlist
            // loaders can give up within milliseconds of each other. Overwriting
            // the handle orphans the first timer, which still fires: two
            // loadSource calls, and the second tears down the MediaSource the
            // first just rebuilt, burning two attempts on one outage.
            if (reconnectTimer !== null) window.clearTimeout(reconnectTimer);
            reconnectTimer = window.setTimeout(() => {
              if (cancelled || !hls) return;
              hls.loadSource(url);
            }, delay);
            return;
          }

          setState('error');
        };
        hls.on(Hls.Events.MANIFEST_PARSED, onManifest);
        hls.on(Hls.Events.FRAG_BUFFERED, onFragBuffered);
        hls.on(Hls.Events.ERROR, onError);
        hls.loadSource(url);
        hls.attachMedia(videoRef.current);
        detach = () => {
          hls?.off(Hls.Events.MANIFEST_PARSED, onManifest);
          hls?.off(Hls.Events.FRAG_BUFFERED, onFragBuffered);
          hls?.off(Hls.Events.ERROR, onError);
        };
      } else if (videoRef.current.canPlayType('application/vnd.apple.mpegurl')) {
        // Safari native HLS
        el.src = url;
        el.addEventListener('loadedmetadata', startPlayback);
        el.addEventListener('error', () => setState('error'));
        detach = () => el.removeEventListener('loadedmetadata', startPlayback);
      } else {
        setState('error');
      }
    });

    return () => {
      cancelled = true;
      window.clearInterval(sample);
      // A pending reconnect outlives the player it was scheduled for otherwise,
      // and fires loadSource on a destroyed instance after the url changed.
      if (reconnectTimer !== null) window.clearTimeout(reconnectTimer);
      detach?.();
      hls?.destroy();
      el.removeAttribute('src');
      el.load();
    };
  }, [url]);

  return (
    <div className="relative h-full w-full">
      <video
        ref={videoRef}
        className={className}
        aria-label={label}
        controls={controls}
        autoPlay
        playsInline
        muted
      />
      {state !== 'playing' && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/70 pointer-events-none">
          {/* Same type and same tokens as the WebRTC branch's overlay: on a
              fallback the word `Connecting…` used to restyle and dim in place
              on the same tile, one second apart. The `--on-media-*` set is
              defined for both themes and holds >=4.5:1 over a scrim on a white
              frame, which the raw greys did not. */}
          <span
            className={`text-[10px] uppercase tracking-[0.25em] ${
              state === 'error' ? 'text-on-media-red-fg' : 'text-on-media-muted-fg'
            }`}
          >
            {state === 'error' ? 'Stream offline' : 'Connecting…'}
          </span>
        </div>
      )}
    </div>
  );
}
